import { useI18N, useLegalCheckbox } from "@sundae/react-hooks";
import { Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback, useMemo } from "react";
import { useHeaderHeight } from "../../../hooks/useHeaderHeight";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";

const MaybeTasteTestDraftStatusOverlay: FC = () => {
  const { t } = useI18N("taste-test");
  const {
    state: {
      tasteTestStatus: {
        isTasteTestDraft,
        isTasteTestReadOnly,
        isTasteTestPending,
        isTasteTestGeofenced,
        isTasteTestModerated,
      },
    },
  } = useTasteTestContext();
  const { headerHeight } = useHeaderHeight();
  const { handleErrorFlash, showErrorFlash } = useLegalCheckbox();

  const handleOverlayClick = useCallback(() => {
    handleErrorFlash();
  }, [handleErrorFlash]);

  const overlayLabel = useMemo(() => {
    if (isTasteTestGeofenced) {
      return t("detail.readOnlyOverlay.geofenced");
    }

    if (isTasteTestModerated) {
      return t("detail.readOnlyOverlay.moderated");
    }

    if (isTasteTestDraft) {
      return t("detail.readOnlyOverlay.draft");
    }

    if (isTasteTestPending) {
      return t("detail.readOnlyOverlay.pending");
    }

    return t("detail.readOnlyOverlay.fallback");
  }, [isTasteTestDraft, isTasteTestGeofenced, isTasteTestModerated, isTasteTestPending, t]);

  const overlayLabelVariant = useMemo(() => {
    if (showErrorFlash) {
      return "forceLight";
    }
    if (isTasteTestGeofenced || isTasteTestModerated) {
      return "error";
    }

    if (isTasteTestPending) {
      return "success";
    }

    if (isTasteTestDraft) {
      return "primary";
    }

    return "normal";
  }, [
    isTasteTestDraft,
    isTasteTestGeofenced,
    isTasteTestModerated,
    isTasteTestPending,
    showErrorFlash,
  ]);

  if (isTasteTestReadOnly) {
    return (
      <div
        className={cx(
          "fixed left-0 z-[999] w-full rounded-xl border-[8px] border-dashed",
          {
            "border-error": isTasteTestGeofenced || isTasteTestModerated,
            "border-success": isTasteTestPending,
            "border-primary": isTasteTestDraft,
          },
          toolkitCx.layout.transition.colors
        )}
        onClick={handleOverlayClick}
        style={{
          top: `${headerHeight}px`,
          height: `calc(100% - ${headerHeight}px)`,
        }}
      >
        <div
          className={cx(
            "absolute top-0 z-20 w-full max-w-[90%] rounded-bl-lg rounded-br-lg border-[8px] border-t-0 border-dashed text-center lg:max-w-[75%]",
            "before:absolute before:left-0 before:top-0 before:z-[-1] before:h-full before:w-full before:transition-colors before:duration-500",
            {
              "border-error": isTasteTestGeofenced || isTasteTestModerated,
              "before:bg-error/30":
                (isTasteTestGeofenced || isTasteTestModerated) && !showErrorFlash,
              "before:bg-error": (isTasteTestGeofenced || isTasteTestModerated) && showErrorFlash,
              "border-primary": isTasteTestDraft,
              "before:bg-primary/30": isTasteTestDraft && !showErrorFlash,
              "before:bg-primary": isTasteTestDraft && showErrorFlash,
              "border-success": isTasteTestPending,
              "before:bg-success/30": isTasteTestPending && !showErrorFlash,
              "before:bg-success": isTasteTestPending && showErrorFlash,
            },
            toolkitCx.layout.background.default,
            toolkitCx.layout.spacing.vertical.md,
            toolkitCx.layout.spacing.horizontal.lg,
            toolkitCx.recipes.center.horizontal
          )}
        >
          <Text className="relative z-20" tag="span" size="lg" variant={overlayLabelVariant}>
            {overlayLabel}
          </Text>
        </div>
      </div>
    );
  }

  return null;
};

export default MaybeTasteTestDraftStatusOverlay;
